/**
 * components/StatusPill
 * ──────────────────────────────────────────────────────────────────────────
 * A non-interactive status chip: `2px 9px`, 999px radius, 11px, capitalised,
 * never wrapping. The three colours come from one status family, so a caller
 * passes a TONE and never four hex values.
 *
 * The register's status column uses the tighter `2px 8px` padding, which is
 * the only variation — hence `dense`.
 *
 * If you find yourself wanting a pill in a colour that is not a status family,
 * the answer is almost always that it is not a status: colour is reserved for
 * status in this design, and everything else is grey.
 * ──────────────────────────────────────────────────────────────────────────
 */
import { StyleSheet, Text, View } from 'react-native';
import { radii } from '../../theme/radii';
import { sans, size as fontSize } from '../../theme/typography';
import { toneOf, type ToneName } from '../../lib/status';
import Dot from '../Dot';

export interface StatusPillProps {
  label: string;
  tone: ToneName;
  /** The register's tighter padding. */
  dense?: boolean;
  /** Show the family's saturated dot before the label. */
  showDot?: boolean;
  /** Off for values that are already correctly cased — ids, references. */
  capitalize?: boolean;
}

export function StatusPill({
  label,
  tone,
  dense = false,
  showDot = false,
  capitalize = true,
}: StatusPillProps) {
  const colors = toneOf(tone);
  return (
    <View
      style={[
        styles.pill,
        {
          paddingHorizontal: dense ? 8 : 9,
          borderColor: colors.border,
          backgroundColor: colors.bg,
        },
      ]}
    >
      {showDot && <Dot tone={tone} />}
      <Text
        numberOfLines={1}
        style={[
          styles.label,
          { color: colors.text },
          capitalize && styles.capitalize,
        ]}
      >
        {label}
      </Text>
    </View>
  );
}

const styles = StyleSheet.create({
  pill: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 5,
    alignSelf: 'flex-start',
    paddingVertical: 2,
    borderWidth: 1,
    borderRadius: radii.pill,
    flexShrink: 0,
  },
  label: {
    ...sans('400'),
    fontSize: fontSize.metaSm,
  },
  capitalize: { textTransform: 'capitalize' },
});

export default StatusPill;
